import { config } from '../config';

/** Erreur renvoyée par l'API au format problem+json, avec son code métier. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
    readonly errors: string[] = [],
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/** Jeton courant (pour les rares lectures binaires hors `api`). */
export function currentAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'dependance_indisponible', 'Le service est injoignable. Vérifiez votre connexion.');
  }
  if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;

  let problem: { title?: string; detail?: string; code?: string; errors?: string[] } = {};
  try {
    problem = await res.json();
  } catch {
    // corps absent ou non JSON
  }
  if (res.status === 401) onUnauthorized?.();
  throw new ApiError(
    res.status,
    problem.code,
    problem.detail ?? problem.title ?? 'Une erreur est survenue.',
    problem.errors ?? [],
  );
}

/** Envoi multipart (pièces jointes). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method: 'POST',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: form,
  });
  if (res.ok) return (await res.json()) as T;
  const problem = (await res.json().catch(() => ({}))) as { detail?: string; title?: string; code?: string };
  throw new ApiError(res.status, problem.code, problem.detail ?? problem.title ?? 'Envoi impossible.');
}

/** Téléchargement authentifié d'un fichier (le jeton ne peut pas passer dans un simple lien). */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, undefined, 'Téléchargement impossible.');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface Me {
  userId: string;
  displayName: string;
  kind: 'agent' | 'usager' | 'organisme' | 'technique';
  mfa: boolean;
  mfaWithheld: number;
  permissions: string[];
  habilitations: {
    id: string;
    profileCode: string;
    organizationId: string;
    scope: 'national' | 'juridictions';
    jurisdictionIds: string[];
    purposes: string[];
    maxSensitivity: number;
    validUntil: string;
    isEmergency: boolean;
  }[];
}

export interface Referentials {
  profiles: {
    code: string;
    label: string;
    restriction: string;
    requires_mfa: boolean;
    max_sensitivity: number;
    permissions: string[];
  }[];
  permissions: { code: string; label: string }[];
  jurisdictions: {
    id: string;
    code: string;
    name: string;
    kind: string;
    is_fictitious: boolean;
    active: boolean;
  }[];
  organizations: {
    id: string;
    code: string;
    name: string;
    kind: string;
    jurisdiction_id: string | null;
    is_fictitious: boolean;
    active: boolean;
  }[];
  purposes: { code: string; label: string; is_fictitious: boolean; active: boolean }[];
  sensitivities: { level: number; code: string; label: string }[];
}

export type HabilitationStatus =
  'demandee' | 'active' | 'suspendue' | 'retiree' | 'refusee' | 'expiree' | 'a_venir';

export interface Habilitation {
  id: string;
  userId: string;
  userDisplayName: string;
  profileCode: string;
  organizationId: string;
  organizationName: string;
  scope: 'national' | 'juridictions';
  jurisdictionIds: string[];
  purposes: string[];
  maxSensitivity: number;
  validFrom: string;
  validUntil: string;
  status: HabilitationStatus;
  isEmergency: boolean;
  reviewRequired: boolean;
  requestReason: string;
  requestedBy: string;
  decidedBy: string | null;
  decisionReason: string | null;
  version: number;
}

export interface UserSummary {
  id: string;
  subject: string;
  display_name: string;
  kind: string;
  status: string;
}
